// src/components/CreatorSidebar.js
// Creator Panel navigation sidebar

import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
    Box,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Drawer,
    Typography,
    Divider,
    useTheme,
    Collapse,
    IconButton,
    CircularProgress,
    Chip,
    Tooltip,
} from '@mui/material';
import ExpandLess from '@mui/icons-material/ExpandLess';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import SettingsIcon from '@mui/icons-material/Settings';
import FolderIcon from '@mui/icons-material/Folder';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import MenuIcon from '@mui/icons-material/Menu';
import * as MuiIcons from '@mui/icons-material';
import { useChatbots } from '../contexts/ChatbotContext';
import { useCompanySettings } from '../contexts/CompanySettingsContext';
import UpgradePlan from './UpgradePlan';
import axiosInstance from '../utils/axiosInstance';
import logoFinal from '../assets/images/logo-final.png';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const collapsedWidth = 60;

const colors = {
    sidebarBg: '#0f172a',
    sidebarBgSecondary: '#1e293b',
    sidebarHover: 'rgba(59, 130, 246, 0.08)',
    sidebarText: '#e2e8f0',
    sidebarTextMuted: '#94a3b8',
    sidebarTextActive: '#ffffff',
    accent: '#3b82f6',
    accentLight: 'rgba(59, 130, 246, 0.15)',
    accentGlow: 'rgba(59, 130, 246, 0.4)',
    border: 'rgba(255, 255, 255, 0.06)',
    success: '#10b981',
};

export default function CreatorSidebar({ drawerWidth, isOpen, onClose, isMobile, toggleSidebar }) {
    const theme = useTheme();
    const location = useLocation();
    const { activeChatbot } = useChatbots();

    const [modules, setModules] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openSections, setOpenSections] = useState({});
    const { companyLogo } = useCompanySettings();

    const userStr = localStorage.getItem("user");
    const user = userStr ? JSON.parse(userStr) : null;
    const userRole = user?.active_team?.user_role_in_team || "";
    // Use team's account type (from team owner) for feature access
    const teamAccountType = user?.active_team?.team_account_type || user?.account_type || "individual";
    const isBusinessAccount = teamAccountType === "business";

    const isChatbotSetupComplete = activeChatbot ? activeChatbot.is_process_completed : false;
    const chatbotIdentity = activeChatbot ? activeChatbot.chatbot_identity : null;
    const hasChatbotSelected = !!activeChatbot;

    // Determine if sidebar is collapsed (not open and not mobile)
    const isCollapsed = !isOpen && !isMobile;
    const currentWidth = isCollapsed ? collapsedWidth : drawerWidth;

    const getIconComponent = (iconName) => {
        if (!iconName) return FolderIcon;
        const IconComponent = MuiIcons[iconName];
        return IconComponent || FolderIcon;
    };

    useEffect(() => {
        const fetchModules = async () => {
            try {
                setLoading(true);
                // Fetch only Creator Panel modules
                const response = await axiosInstance.get('/api/modules?panel=creator');

                if (response.data.success) {
                    const parsed = JSON.parse(response.data.data);
                    setModules(parsed.modules);

                    // Initialize all sections as closed
                    const initialSections = {};
                    parsed.modules.forEach((module) => {
                        initialSections[module.slug] = false;
                    });
                    setOpenSections(initialSections);
                }
            } catch (error) {
                console.error('Error fetching modules:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchModules();
    }, []);

    // Auto-open parent section when child route is active
    useEffect(() => {
        if (modules.length === 0) return;

        const currentPath = location.pathname;

        modules.forEach(module => {
            const hasSubmodules = module.submodules && module.submodules.length > 0;

            if (hasSubmodules) {
                // Check if any submodule matches current path
                const isChildActive = module.submodules.some(sub => {
                    const subPath = sub.is_chatbot_specific
                        ? `/chatbot/${chatbotIdentity}/${sub.slug}`
                        : `/${sub.slug}`;
                    return currentPath === subPath || currentPath.startsWith(subPath + '/');
                });

                if (isChildActive) {
                    setOpenSections(prev => ({ ...prev, [module.slug]: true }));
                }
            } else {
                // Check if module itself matches
                const modulePath = module.is_chatbot_specific
                    ? `/chatbot/${chatbotIdentity}/${module.slug}`
                    : `/${module.slug}`;
                const isActive = currentPath === modulePath || currentPath.startsWith(modulePath + '/');

                if (isActive) {
                    setOpenSections(prev => ({ ...prev, [module.slug]: true }));
                }
            }
        });
    }, [location.pathname, modules, chatbotIdentity]);

    const getLinkProps = (isFeatureLink = true, isSystemModule = false, isChatbotComplete = false) => {
        if (isFeatureLink && !isSystemModule &&  !isChatbotComplete) {
            return { onClick: (e) => e.preventDefault() };
        }
        return { onClick: handleDrawerToggle };
    };

    const handleSectionToggle = (section) => {
        if (isCollapsed) return; // Don't toggle sections when collapsed
        setOpenSections(prevState => ({
            ...prevState,
            [section]: !prevState[section],
        }));
    };

    const handleDrawerToggle = () => {
        if (isMobile && onClose) {
            onClose();
        }
    };

    // Check if a path is active
    const isPathActive = (path) => {
        return location.pathname === path || location.pathname.startsWith(path + '/');
    };

    const renderModuleItem = (module) => {
        const path = (module.is_chatbot_specific) ? `chatbot/${chatbotIdentity}/${module.route}` : module.route;
        const isSystemModule = module.is_system_module
        const fullPath = `/${path}`;
        const IconComponent = getIconComponent(module.icon);
        const isActive = isPathActive(fullPath);

        const itemContent = (
            <ListItem key={module.id} disablePadding sx={{ position: 'relative' }}>
                {/* Active indicator bar */}
                {isActive && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            width: 3,
                            height: '60%',
                            bgcolor: colors.accent,
                            borderRadius: '0 4px 4px 0',
                            boxShadow: `0 0 8px ${colors.accentGlow}`,
                        }}
                    />
                )}
                <ListItemButton
                    component={NavLink}
                    to={path}
                    sx={{
                        pl: isCollapsed ? 0 : 3,
                        pr: isCollapsed ? 0 : undefined,
                        py: 1,
                        mx: isCollapsed ? 0.5 : 1.5,
                        borderRadius: '8px',
                        justifyContent: isCollapsed ? 'center' : 'flex-start',
                        color: isActive ? colors.sidebarTextActive : colors.sidebarText,
                        opacity: !isSystemModule && !isChatbotSetupComplete ? 0.4 : 1,
                        cursor: !isSystemModule && !isChatbotSetupComplete ? 'not-allowed' : 'pointer',
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        bgcolor: isActive ? colors.accentLight : 'transparent',
                        '&:hover': {
                            bgcolor: (isSystemModule || isChatbotSetupComplete)
                                ? (isActive ? colors.accentLight : colors.sidebarHover)
                                : 'transparent',
                            transform: isChatbotSetupComplete && !isCollapsed ? 'translateX(2px)' : 'none',
                        },
                    }}
                    {...getLinkProps(true, isSystemModule, isChatbotSetupComplete)}
                    disabled={ !isSystemModule && !isChatbotSetupComplete }
                >
                    <ListItemIcon
                        sx={{
                            minWidth: isCollapsed ? 'auto' : 32,
                            color: isActive ? colors.accent : 'inherit',
                            transition: 'color 0.2s ease',
                        }}
                    >
                        <IconComponent sx={{ fontSize: '1.1rem' }} />
                    </ListItemIcon>
                    {!isCollapsed && (
                        <ListItemText
                            primary={module.name}
                            primaryTypographyProps={{
                                sx: {
                                    fontFamily,
                                    fontSize: '0.8125rem',
                                    fontWeight: isActive ? 600 : 500,
                                    letterSpacing: '-0.01em',
                                }
                            }}
                        />
                    )}
                </ListItemButton>
            </ListItem>
        );

        if (isCollapsed) {
            return (
                <Tooltip key={module.id} title={module.name} placement="right" arrow>
                    {itemContent}
                </Tooltip>
            );
        }

        return itemContent;
    };

    const renderModuleSection = (module) => {
        // Check if module is chatbot-specific and no chatbot is selected
        if (module.is_chatbot_specific && !hasChatbotSelected) {
            return null;
        }

        const hasSubmodules = module.submodules && module.submodules.length > 0;

        // Filter submodules based on is_chatbot_specific
        const visibleSubmodules = hasSubmodules
            ? module.submodules.filter(sub => !sub.is_chatbot_specific || hasChatbotSelected)
            : [];

        // If module has submodules but none are visible, don't render the section
        if (hasSubmodules && visibleSubmodules.length === 0) {
            return null;
        }

        const isOpen = openSections[module.slug];

        // In collapsed mode, show items directly without section headers
        if (isCollapsed) {
            return (
                <React.Fragment key={module.id}>
                    <Divider sx={{ borderColor: colors.border, mx: 1.5, my: 1 }} />
                    {hasSubmodules
                        ? visibleSubmodules.map(submodule => renderModuleItem(submodule))
                        : renderModuleItem(module)
                    }
                </React.Fragment>
            );
        }

        return (
            <React.Fragment key={module.id}>
                <ListItem disablePadding sx={{ mt: 1.5 }}>
                    <ListItemButton
                        onClick={() => handleSectionToggle(module.slug)}
                        sx={{
                            py: 0.75,
                            px: 2,
                            '&:hover': { bgcolor: 'transparent' },
                        }}
                    >
                        <Typography sx={{
                            fontFamily,
                            fontSize: '0.725rem',
                            fontWeight: 700,
                            textTransform: 'uppercase',
                            letterSpacing: '0.08em',
                            color: colors.sidebarTextMuted,
                            flex: 1,
                        }}>
                            {module.name}
                        </Typography>
                        <IconButton
                            size="small"
                            sx={{
                                color: colors.sidebarTextMuted,
                                p: 0.25,
                                transition: 'transform 0.2s ease',
                                transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                            }}
                        >
                            <ExpandLess sx={{ fontSize: '1rem' }} />
                        </IconButton>
                    </ListItemButton>
                </ListItem>
                <Collapse in={isOpen} timeout={200} unmountOnExit>
                    <List component="div" disablePadding sx={{ pb: 0.5 }}>
                        {hasSubmodules
                            ? visibleSubmodules.map(submodule => renderModuleItem(submodule))
                            : renderModuleItem(module)
                        }
                    </List>
                </Collapse>
            </React.Fragment>
        );
    };



    // For mobile, keep original behavior (temporary drawer that closes)
    // For desktop, always show drawer but toggle between expanded/collapsed
    const drawerVariant = isMobile ? 'temporary' : 'permanent';
    const shouldShowDrawer = isMobile ? isOpen : true;

    return (
        <Drawer
            sx={{
                '& .MuiDrawer-paper': {
                    width: currentWidth,
                    boxSizing: 'border-box',
                    background: `linear-gradient(180deg, ${colors.sidebarBg} 0%, ${colors.sidebarBgSecondary} 100%)`,
                    color: colors.sidebarText,
                    borderRight: 'none',
                    top: { xs: 56, sm: 64 },
                    height: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    transition: 'width 0.3s ease',
                    '&::-webkit-scrollbar': { width: '3px' },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        '&:hover': { background: 'rgba(255,255,255,0.2)' },
                    },
                },
            }}
            variant={drawerVariant}
            anchor="left"
            open={shouldShowDrawer}
            onClose={onClose}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Logo Header */}
                <Box sx={{
                    px: isCollapsed ? 0.5 : 2,
                    py: 2.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: isCollapsed ? 'center' : 'space-between',
                    borderBottom: `1px solid ${colors.border}`,
                    overflow: 'visible',
                }}>
                    {isCollapsed ? (
                        // Collapsed: Show expand button
                        <IconButton
                            onClick={toggleSidebar}
                            sx={{
                                width: 40,
                                height: 40,
                                borderRadius: '8px',
                                bgcolor: colors.accent,
                                color: '#fff',
                                '&:hover': {
                                    bgcolor: colors.accent,
                                    opacity: 0.9,
                                },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    ) : (
                        // Expanded: Show logo with collapse button
                        <>
                            <img
                                src={companyLogo || logoFinal}
                                alt="Logo"
                                style={{
                                    height: 45,
                                    width: 'auto',
                                    borderRadius: 0,
                                }}
                                onError={(e) => { e.target.onerror = null; e.target.src = logoFinal; }}
                            />
                            <IconButton
                                onClick={toggleSidebar}
                                size="small"
                                sx={{ color: colors.sidebarTextMuted }}
                            >
                                <MenuOpenIcon />
                            </IconButton>
                        </>
                    )}
                </Box>

                {/* Navigation */}
                <List component="nav" sx={{ flexGrow: 1, pt: 1, pb: 2 }}>

                    {/* Loading State */}
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress size={20} sx={{ color: colors.accent }} />
                        </Box>
                    )}

                    {/* Dynamic Modules */}
                    {!loading && modules.map(module => renderModuleSection(module))}
                </List>

                {/* Upgrade Plan Section - Hide when collapsed */}
                {!isCollapsed && (
                    <Box sx={{ mt: 'auto' }}>
                        <Divider sx={{ borderColor: colors.border, mx: 2 }} />
                        <UpgradePlan />
                    </Box>
                )}
            </Box>
        </Drawer>
    );
}

// Export collapsed width for use in Layout
export { collapsedWidth };
